import './App.css';
import { useState, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import { createTheme, GlobalFonts } from './Theme/theme';
import Dashboard from './Common/Dashboard';
import NavBar from './Common/NavBar';
import Sidebar from './Common/Sidebar';
import Footer from './Common/Footer';
import LoginPage from './Common/LoginPage';
import { Container } from './Components/UI/Containers';
import styled from 'styled-components';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Sitemanagement from './Components/Sitemanagement/Sitemanagementpage/Sitemanagement';
import Attendance from './Components/Attendance/Attendancepage/Attendance';
import Usermanagement from './Components/Usermanagement/Usermanagementpage/Usermanagement';
import { AuthProvider, useAuth } from './Context/AuthContext';
import { PopupProvider } from './Popups/PopupContext'; // Import PopupProvider
import DepartmentsMain from './Components/Departments/Main/DepartmentsMain'; // Import DepartmentsMain
import Leaves from './Components/Leaves/Routes/Leaves';


const MainContent = styled.div`
  transition: margin-left 0.3s ease-in-out;
  padding-top: 10px; /* Adjust based on NavBar height */
  min-height: calc(100vh - 60px); /* Full height minus footer */
  display: flex;
  flex-direction: column;

  ${({ sidebarOpen }) => sidebarOpen && `
    @media (min-width: 769px) {
      margin-left: 250px; /* Width of the sidebar */
    }
  `}
`;

const ContentWrapper = styled.div`
  flex-grow: 1; /* Takes up available space */
`;

// Component to handle routing and layout using AuthContext
function AppLayout() {
  const { isLoggedIn, user, logout } = useAuth(); // Get auth state from context
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Effect to close sidebar on window resize (optional)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';

  return (
    <>
      {isLoggedIn && (
        <Sidebar 
          isOpen={sidebarOpen} 
          toggleSidebar={toggleSidebar} 
          isLoggedIn={isLoggedIn}
          userName={userName}
          onLogout={logout} // Pass logout from context
        />
      )}
      <MainContent sidebarOpen={isLoggedIn && sidebarOpen}> 
        {isLoggedIn && (
          <NavBar 
            toggleSidebar={toggleSidebar} 
            isLoggedIn={isLoggedIn}
            userName={userName}
            onLogout={logout} // Pass logout from context
          />
        )}
        <ContentWrapper>
          <Container>
            <Routes>
              <Route 
                path="/login" 
                element={
                  isLoggedIn 
                    ? <Navigate to="/dashboard" /> 
                    : <LoginPage /> // Remove onLoginSuccess prop
                } 
              />
              <Route 
                path="/" 
                element={isLoggedIn ? <Dashboard /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/dashboard/*" 
                element={isLoggedIn ? <Dashboard /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/sitemanagement/*" 
                element={isLoggedIn ? <Sitemanagement /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/attendance/*" 
                element={isLoggedIn ? <Attendance /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/leaves/*" 
                element={isLoggedIn ? <Leaves /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/usermanagement/*" 
                element={isLoggedIn ? <Usermanagement /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/departmentmanagement/*" 
                element={isLoggedIn ? <DepartmentsMain /> : <Navigate to="/login" />} 
              />
              <Route path="*" element={<Navigate to={isLoggedIn ? "/dashboard" : "/login"} />} />
            </Routes>
          </Container>
        </ContentWrapper>
        {isLoggedIn && <Footer />} 
      </MainContent>
    </>
  );
}


function App() {
  const theme = createTheme();
  
  return (
    <ThemeProvider theme={theme}>
      <GlobalFonts />
      <BrowserRouter>
        <AuthProvider> {/* Wrap the layout with AuthProvider */}
           <PopupProvider> 
          <AppContent /> 
           </PopupProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

// New component to access useAuth hook for conditional rendering of routes
function AppContent() {
  const { isLoggedIn, isLoading } = useAuth();

  if (isLoading) {
    // Optional: Add a global loading spinner if auth check is async
    return <div>Loading authentication...</div>; 
  }

  return (
    <Routes>
      <Route 
        path="/login" 
        element={isLoggedIn ? <Navigate to="/dashboard" replace /> : <LoginPage />} 
      />
      
      {/* All other authenticated routes go through AppLayout */}
      <Route 
        path="/*" 
        element={isLoggedIn ? <AppLayout /> : <Navigate to="/login" replace />} 
      />
    </Routes>
  );
}

export default App;
